import { logEvent, setUserId, setUserProperties, setDefaultEventParameters } from 'firebase/analytics';
import { analytics } from '../firebase';

class AnalyticsService {
    constructor() {
        this.isInitialized = false;
        this.sessionId = this.generateSessionId();
        this.userId = null;
    }

    // Initialize analytics and track session start
    initialize(userId = null) {
        if (!analytics) {
            console.warn('Firebase Analytics not available');
            return;
        }

        // Check if analytics is disabled via environment variable
        if (process.env.REACT_APP_DISABLE_ANALYTICS === 'true') {
            console.log('Analytics disabled via REACT_APP_DISABLE_ANALYTICS environment variable');
            return;
        }

        console.log('Analytics initializing...', { userId, analytics });
        this.isInitialized = true;
        this.userId = userId;

        // Set user ID if provided
        if (userId) {
            setUserId(analytics, userId);
            console.log('User ID set:', userId);
        }

        // Set app version as user property and default event parameter
        const appVersion = process.env.REACT_APP_BUILD_SHA || 'development';
        setUserProperties(analytics, {
            app_version: appVersion
        });
        setDefaultEventParameters(analytics, {
            app_version: appVersion
        });
        console.log('User properties set:', { app_version: appVersion });

        // Track session start
        this.trackEvent('session_start', {
            session_id: this.sessionId,
            app_version: appVersion,
            timestamp: Date.now()
        });

        // Track referral source
        this.trackReferralSource();
    }

    // Generate unique session ID
    generateSessionId() {
        return `session_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
    }

    // Track page views
    trackPageView(pageName, pageTitle = null) {
        if (!this.isInitialized || !analytics) return;

        const appVersion = process.env.REACT_APP_BUILD_SHA || 'development';
        const eventParams = {
            page_title: pageTitle || pageName,
            page_location: window.location.href,
            page_path: window.location.pathname,
            session_id: this.sessionId,
            app_version: appVersion
        };

        if (this.userId) {
            eventParams.user_id = this.userId;
        }

        logEvent(analytics, 'page_view', eventParams);
    }

    // Track referral source
    trackReferralSource() {
        if (!this.isInitialized || !analytics) return;

        const referrer = document.referrer;
        const utmSource = this.getUrlParameter('utm_source');
        const utmMedium = this.getUrlParameter('utm_medium');
        const utmCampaign = this.getUrlParameter('utm_campaign');

        const referralData = {
            referrer: referrer || 'direct',
            utm_source: utmSource || null,
            utm_medium: utmMedium || null,
            utm_campaign: utmCampaign || null,
            session_id: this.sessionId
        };

        if (this.userId) {
            referralData.user_id = this.userId;
        }

        logEvent(analytics, 'referral_source', referralData);

        // Set user properties for referral tracking
        if (utmSource) {
            setUserProperties(analytics, {
                utm_source: utmSource,
                utm_medium: utmMedium,
                utm_campaign: utmCampaign
            });
        }
    }

    // Track custom events
    trackEvent(eventName, parameters = {}) {
        if (!this.isInitialized || !analytics) {
            console.warn('Analytics not initialized or not available', { isInitialized: this.isInitialized, analytics });
            return;
        }

        const appVersion = process.env.REACT_APP_BUILD_SHA || 'development';
        const eventParams = {
            ...parameters,
            session_id: this.sessionId,
            app_version: appVersion,
            timestamp: Date.now()
        };

        if (this.userId) {
            eventParams.user_id = this.userId;
        }

        console.log('Tracking event:', eventName, eventParams);
        logEvent(analytics, eventName, eventParams);
    }

    // Track user actions
    trackUserAction(action, details = {}) {
        this.trackEvent('user_action', {
            action,
            ...details
        });
    }

    // Track route selection
    trackRouteSelection(routeId, routeName) {
        this.trackEvent('route_selected', {
            route_id: routeId,
            route_name: routeName
        });
    }

    // Track authentication events
    trackAuthEvent(eventType, method = null) {
        this.trackEvent('auth_event', {
            event_type: eventType,
            method
        });
    }

    // Track app settings changes
    trackSettingsChange(setting, value) {
        this.trackEvent('settings_change', {
            setting,
            value: typeof value === 'object' ? JSON.stringify(value) : value
        });
    }

    // Get URL parameter
    getUrlParameter(name) {
        const urlParams = new URLSearchParams(window.location.search);
        return urlParams.get(name);
    }

    // Update user ID (for when user logs in)
    setUserId(userId) {
        this.userId = userId;
        if (analytics) {
            setUserId(analytics, userId);
        }
    }

    // Track session end
    trackSessionEnd() {
        if (!this.isInitialized || !analytics) return;

        this.trackEvent('session_end', {
            session_id: this.sessionId,
            duration: Date.now() - parseInt(this.sessionId.split('_')[1])
        });
    }
}

// Create singleton instance
const analyticsService = new AnalyticsService();

export default analyticsService;
